import { v4 as uuid } from 'uuid';
import { defineStoredJson } from '../../utils/storage';
import { authFetch, getToken } from '../../utils/authFetch';
import {
    bootLines,
    databaseOpenLines,
    deviceLines,
    storageLines
} from './diagnostics';
import { unsentAttachment } from './salvage';

/**
 * Fault reports, sent by the app about itself — because the faults worth hearing
 * about are the ones nobody reports: a start-up that hangs, a sync failing since
 * Tuesday, a white screen. By the time one reaches the office it is "es ging halt
 * nicht", with no version, no message and no trail.
 *
 * Sending by itself settles the whole design. A report may not be able to go
 * anywhere — offline is the normal state in the field, and exactly the state a
 * sync fault happens in — so it is KEPT and sent later. And it must not flood: a
 * failing replication retries every five seconds and a released fault is on
 * forty devices at once, so this device sends the same fault once a day at most.
 *
 * A report carries the work still on the device with it, as a file (see
 * `salvage`): the half that cannot be reproduced once somebody presses reset.
 */

/** Where the report came from. Must match the server's own list. */
export type ReportKind = 'database' | 'sync' | 'crash' | 'manual';

export interface ProblemReport {
    kind: ReportKind;
    /** The one line that becomes the mail's subject. */
    headline: string;
    /**
     * The stable part of the fault — collection, code, message. The server
     * fingerprints this together with the kind and the version, so it must NOT
     * carry a timestamp, a queue length or anything else that moves.
     */
    signature: string;
    /** Everything worth knowing, as text. */
    body: string;
    /** What the person wrote, when they reported by hand. */
    description?: string;
}

interface QueuedReport extends ProblemReport {
    /**
     * Identity within the queue. Needed because the queue survives a reload:
     * after it has been through storage the entries are fresh objects, so the
     * one that just went out cannot be recognised by reference.
     */
    id: string;
    /** When it happened, which may be long before it is sent. */
    occurredAt: string;
    appVersion: string;
}

/** What became of a report, for the screen that triggered it. */
export type ReportState = 'sending' | 'sent' | 'queued' | 'off';

/**
 * Reports waiting for a connection, or for somebody to sign in again.
 *
 * Small and few by construction: the caps below see to that, and a report is a
 * few kilobytes of text.
 */
const queue = defineStoredJson<QueuedReport[]>({
    key: 'problem_reports',
    fallback: () => [],
    /**
     * Kept through a wipe. The recovery offered on a broken start-up is
     * "lokale Daten zurücksetzen", so a wipe is the LIKELIEST next thing to
     * happen after a report is queued — throwing the evidence away at exactly
     * that moment would empty the feature of its point.
     */
    keepOnWipe: true,
    decode: (parsed) =>
        Array.isArray(parsed) ? (parsed as QueuedReport[]) : undefined
});

/** When each fault was last sent from THIS device, keyed by its fingerprint. */
const sentAt = defineStoredJson<Record<string, number>>({
    key: 'problem_reports_sent',
    fallback: () => ({}),
    decode: (parsed) =>
        parsed && typeof parsed === 'object' && !Array.isArray(parsed)
            ? (parsed as Record<string, number>)
            : undefined
});

/** Whether the app may report on its own. Off is a deliberate, kept choice. */
const enabled = defineStoredJson<boolean>({
    key: 'problem_reports_enabled',
    fallback: () => true,
    decode: (parsed) => (typeof parsed === 'boolean' ? parsed : undefined)
});

export function automaticReportsEnabled(): boolean {
    return enabled.read();
}

export function setAutomaticReports(on: boolean): void {
    enabled.write(on);
}

/** The same fault, once a day from one device. The server does the rest. */
const REPEAT_QUIET_MS = 24 * 60 * 60 * 1000;

/**
 * A ceiling for the device that has gone wrong in a NEW way each time — a
 * corrupted local database manages that, and its reports are the least useful
 * ones there are.
 */
const MAX_PER_DAY = 10;

/** Past this the queue is a symptom, not an outbox. Oldest go first. */
const MAX_QUEUED = 20;

/** kind + version + signature, matching how the server groups them. */
function fingerprintOf(report: ProblemReport): string {
    return `${report.kind}|${__APP_VERSION__}|${report.signature}`;
}

/**
 * Whether this fault may be sent from this device now, and note that it was. The
 * record is kept even when the send fails: what is limited is how often a fault
 * is REPORTED, not how often it reaches the server.
 */
function claimSendSlot(report: ProblemReport): boolean {
    const now = Date.now();
    const history = sentAt.read();
    const fingerprint = fingerprintOf(report);

    // Yesterday's entries are neither a limit nor worth keeping.
    const recent = Object.fromEntries(
        Object.entries(history).filter(([, at]) => now - at < REPEAT_QUIET_MS)
    );
    if (recent[fingerprint] !== undefined) {
        return false;
    }
    if (Object.keys(recent).length >= MAX_PER_DAY) {
        return false;
    }
    // A refused write means the cap cannot be remembered, and a cap nobody
    // remembers is no cap: the same fault would be reported on every retry for
    // as long as the store stays full. Better one report lost than a device
    // that reports for ever.
    return sentAt.write({ ...recent, [fingerprint]: now });
}

/**
 * Report a fault. Never throws and never blocks the caller — it is called from
 * the middle of something already going wrong. Returns what became of it, for a
 * screen that wants to say so.
 */
export async function reportProblem(
    report: ProblemReport
): Promise<ReportState> {
    try {
        if (!enabled.read()) {
            return 'off';
        }
        if (!claimSendSlot(report)) {
            // Already reported today. Not an error and not worth saying.
            return 'sent';
        }
        const queued: QueuedReport = {
            ...report,
            id: uuid(),
            appVersion: __APP_VERSION__,
            occurredAt: new Date().toISOString()
        };
        if (await send(queued)) {
            return 'sent';
        }
        queue.write([...queue.read(), queued].slice(-MAX_QUEUED));
        return 'queued';
    } catch {
        // A report about a fault must never raise one.
        return 'queued';
    }
}

/**
 * Try one report. False means "keep it": no connection, no token, or a server
 * that could not take it. A refusal the report ITSELF caused — malformed, or
 * over the account's hourly limit — returns TRUE, or it would retry for ever.
 */
async function send(report: QueuedReport): Promise<boolean> {
    if (getToken() === null) {
        return false;
    }
    // Collected HERE rather than when the report was made: a queued report
    // waits in localStorage, and a day's field work does not belong there
    // (see `unsentAttachment`). What is still outstanding NOW is also the
    // truthful answer — some of it may have gone up in the meantime.
    const attachment = await unsentAttachment();

    const post = async (withAttachment: boolean) =>
        authFetch('/api/reports', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                ...report,
                attachment: withAttachment ? attachment?.content : undefined,
                attachmentName: withAttachment
                    ? attachment?.fileName
                    : undefined
            })
        });

    try {
        let response = await post(attachment !== null);
        // Too large for the server to accept. The attachment is what made it
        // large, and the REPORT is what says something is wrong — so the report
        // goes on its own rather than both being thrown away. The work itself
        // is still on the device and still in the file.
        if (response.status === 413 && attachment) {
            response = await post(false);
        }
        if (response.ok) {
            return true;
        }
        // 400 means this report is malformed and always will be; keeping it
        // would mean retrying it for ever. Everything else — an expired token,
        // a rate limit, a server having a bad minute — is worth another go, and
        // dropping it would take the rescued work with it.
        return response.status === 400;
    } catch {
        return false;
    }
}

let flushing = false;

/**
 * Send whatever is waiting, oldest first, and stop at the first one that will
 * not go: they are all facing the same server, so the second would only fail
 * the same way.
 */
export async function flushReports(): Promise<void> {
    if (flushing) {
        return;
    }
    flushing = true;
    try {
        for (;;) {
            const waiting = queue.read();
            if (waiting.length === 0) {
                return;
            }
            const oldest = waiting[0];
            if (!(await send(oldest))) {
                return;
            }
            // Re-read rather than writing back what we started with: another tab
            // may have queued something while this one was on the wire. A refused
            // write leaves the entry at the head of the queue, so going round
            // again would re-send it whole — stop, and let the next flush try.
            const stored = queue.write(
                queue.read().filter((item) => item.id !== oldest.id)
            );
            if (!stored) {
                return;
            }
        }
    } finally {
        flushing = false;
    }
}

export function queuedReportCount(): number {
    return queue.read().length;
}

/**
 * Start emptying the queue: when the device comes back online, and otherwise
 * every few minutes. The interval is not impatience — a device can be online the
 * whole time and still have had no token when the report was made.
 */
export function startReportSender(): () => void {
    const flush = () => void flushReports();
    flush();
    window.addEventListener('online', flush);
    const timer = window.setInterval(flush, 5 * 60 * 1000);
    return () => {
        window.removeEventListener('online', flush);
        window.clearInterval(timer);
    };
}

/**
 * The ways the local database fails to come up. `blocked` is its own case on
 * purpose: it is not a slow device but a connection an interrupted start left
 * behind, and the cure is to close the window — waiting and resetting both do
 * nothing for it (see db/openStall.ts).
 */
export type DatabaseOutcome = 'stalled' | 'blocked' | 'failed';

const DATABASE_HEADLINE: Record<DatabaseOutcome, string> = {
    stalled: 'Lokale Datenbank braucht ungewöhnlich lange',
    blocked: 'Lokale Datenbank ist von einem früheren Start belegt',
    failed: 'Lokale Datenbank konnte nicht geöffnet werden'
};

/**
 * The database would not open, or took so long that it amounts to the same. The
 * signature carries the reason and nothing else, so twenty reloads of the same
 * broken migration are one fault; the body carries the boot trail, which says
 * WHICH phase never finished.
 */
export async function reportDatabaseProblem(
    outcome: DatabaseOutcome,
    message: string
): Promise<ReportState> {
    const headline = DATABASE_HEADLINE[outcome];
    // Taken before anything is awaited: the open may finish meanwhile, and the
    // report is about the moment it was made.
    const traceLines = [...bootLines(), ...databaseOpenLines()];
    const environment = await storageLines();
    return reportProblem({
        kind: 'database',
        headline,
        signature: `db|${outcome}|${message}`,
        body: [
            headline,
            '',
            ...deviceLines(),
            ...traceLines,
            ...environment,
            '',
            message === ''
                ? 'Keine Fehlermeldung — die Öffnung läuft noch.'
                : message
        ].join('\n')
    });
}
